import { Component } from "react";
import type { ErrorInfo, ReactNode } from "react";
import { translate } from "../i18n/hooks.ts";
import { readSavedLanguage } from "../i18n/language.ts";

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

/** Without this, an uncaught exception unmounts the whole React tree and leaves a blank screen with no message at all. */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("Uncaught error:", error, info.componentStack);
  }

  render() {
    if (this.state.error) {
      // If App.tsx crashed, its LanguageProvider unmounted too, so the
      // language must be read directly instead of via the hook.
      const language = readSavedLanguage();
      return (
        <div className="fatal-error-screen">
          <div className="fatal-error-screen-card">
            <h1>{translate(language, "errorBoundary.title")}</h1>
            <p>{translate(language, "errorBoundary.message")}</p>
            {/* The crash message itself is a genuinely unpredictable browser/
                third-party string (unlike the engine's own TextKey-backed
                warnings) that can't be translated ahead of time — this only
                localizes the SENTENCE STRUCTURE around it, same pattern as
                storage.errorMessage/gisImport.readFailed/
                projectionPicker.searchFailed. */}
            <pre>
              {translate(language, "errorBoundary.detail", { message: this.state.error.message })}
            </pre>
            <button className="button button-primary" onClick={() => window.location.reload()}>
              {translate(language, "errorBoundary.reload")}
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
